/*eslint-disable*/
import moment from 'moment';
import { ShoppingBag, Package } from 'lucide-react';

export const DEFAULT_FLOW_MODE = 'productionPlanCombined';

export const stepModes = {
	productionPlanCombined: {
		heading: 'Review Orders & Plan Production',
		categories: [],
		prefetch: 'orderTables',
		initialState: {
			planName: `Plan ${moment(new Date()).format('DD MMM')}`,
			fromStore: null,
			planDate: new Date(),
		},
		fields: [
			{
				id: 'planName',
				label: 'Plan Name',
				type: 'text',
				required: true,
				placeholder: 'Enter plan name',
			},
			{
				id: 'fromStore',
				label: 'Select Kitchen',
				type: 'tabs',
				required: true,
				dataKey: 'storeList',
				filterBy: { key: 'type', value: 'BASE' },
				wrapperClass: 'sm:col-span-2 lg:col-span-3',
			},
			{
				id: 'planDate',
				label: 'Select Date',
				type: 'date',
				required: true,
				shouldDisableDate: true,
			},
		],
		extraInitialState: {
			currentStep: 1,
		},
		steps: [
			{
				id: 1,
				key: 'basicDetails',
				title: 'Basic Details',
				subtitle: 'Kitchen, date & plan name',
				type: 'form',
				card: {
					title: 'Basic Details',
					subtitle: 'Choose the kitchen, date and plan name for this production plan.',
					layout: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4',
				},
				fields: [
					'planName',
					'planDate',
					'fromStore',
				],
				fieldApis: {
					onMount: ['fromStore', 'buyer'],
					onChange: {},
				},
				fieldIcons: {
					planName: 'clipboard',
					planDate: 'calendar',
					fromStore: 'kitchen',
				},
				requiredFields: [
					'planName',
					'planDate',
					'fromStore',
				],
				buttons: [
					{
						id: 'nextStep',
						label: 'View Orders',
						noDefineLabel: true,
						type: 'primary',
						style: 'bg-amber-400 hover:bg-amber-500 text-gray-900',
						icon: 'arrowRight',
						position: 'right',
						action: 'nextStep',
						validators: ['requiredFields'],
					},
				],
			},
			{
				id: 2,
				key: 'buyerOrdersTable',
				title: 'Buyer Orders',
				subtitle: 'Buyer wise quantities',
				type: 'table',
				tableKey: 'buyerOrders',
				refetchWhenChanged: ['fromStore', 'planDate'],
				onUpdate: 'orderItems',
				hideWhenEmpty: true,
				emptyMessage:
					'No pending dispatch buyer orders for the selected store and date',
				cellType: 'number',
				tableConfig: {
					showGroupedHeaders: true,
					groupHeaderColor: '#fffbeb',
					groupHeaderTextColor: '#92400e',
					coreGroupLabel: 'Core Info',
					summaryGroupLabel: 'Summary',
				},
				functionality: ['drillDownProducts'],
				pasteMode: 'buyerOrder',
				buttons: [
					{
						id: 'previousStep',
						label: 'Back',
						type: 'outline',
						style: 'border-gray-400 text-gray-600 hover:bg-gray-100',
						icon: 'arrowLeft',
						position: 'left',
						action: 'prevStep',
						noDefineLabel: true,
					},
					{
						id: 'nextStep',
						label: 'Next Order',
						noDefineLabel: true,
						type: 'primary',
						style: 'bg-amber-400 hover:bg-amber-500 text-gray-900',
						icon: 'arrowRight',
						position: 'right',
						action: 'nextStep',
					},
					{
						id: 'generateProductionPlan',
						label: 'Initiate Production Plan',
						noDefineLabel: true,
						type: 'secondary',
						icon: 'plan',
						style: 'border border-amber-300 bg-amber-100 px-4 font-semibold text-gray-700 hover:bg-amber-200',
						position: 'right',
					},
				],
			},
			{
				id: 3,
				key: 'internalSalesOrder',
				title: 'Internal Sales Orders',
				subtitle: 'Store wise order quantities',
				type: 'table',
				tableKey: 'internalSales',
				refetchWhenChanged: ['fromStore', 'planDate'],
				onUpdate: 'orderItems',
				hideWhenEmpty: true,
				cellType: 'number',
				functionality: ['drillDownProducts'],
				buttons: [
					{
						id: 'previousStep',
						label: 'Back',
						type: 'outline',
						style: 'border-gray-400 text-gray-600 hover:bg-gray-100',
						icon: 'arrowLeft',
						position: 'left',
						action: 'prevStep',
						noDefineLabel: true,
					},
					{
						id: 'nextStep',
						label: 'Next Order',
						noDefineLabel: true,
						type: 'primary',
						style: 'bg-amber-400 hover:bg-amber-500 text-gray-900',
						icon: 'arrowRight',
						position: 'right',
						action: 'nextStep',
					},
					{
						id: 'generateProductionPlan',
						label: 'Initiate Production Plan',
						noDefineLabel: true,
						type: 'secondary',
						icon: 'plan',
						style: 'border border-amber-300 bg-amber-100 px-4 font-semibold text-gray-700 hover:bg-amber-200',
						position: 'right',
					},
				],
			},
		],
	},
};


export const getStepConfig = (modeName) => stepModes?.[modeName] || null;

export const getStepperSteps = (modeName) =>
	(getStepConfig(modeName)?.steps || []).map(({ id, title, subtitle }) => ({
		id,
		title,
		subtitle,
	}));
export const ORDER_CATEGORIES = [
	{
		id: 1,
		key: 'internalSales',
		label: 'Internal Sales',
		description: 'Internal order between your own stores — choose the order type (PO, CMS, CMS Indent) in the details',
		icon: Package,
	},
	{
		id: 2,
		key: 'buyerOrder',
		label: 'Buyer Order',
		description: 'Create orders for multiple buyers at once',
		icon: ShoppingBag,
	},
];

export const categoryOf = (key) => ORDER_CATEGORIES.find((c) => c.key === key);

export const primaryCategoryKey = (keys = []) =>
	ORDER_CATEGORIES.map((c) => c.key).find((key) => keys.includes(key))
	|| keys[0];


export const passesIncludeIf = (includeIf, selectedKeys = []) => {
	if (!includeIf) return true;
	const { categorySelected, categoryNotSelected } = includeIf;
	if (categorySelected && !selectedKeys.includes(categorySelected)) {
		return false;
	}
	if (categoryNotSelected && selectedKeys.includes(categoryNotSelected)) {
		return false;
	}
	return true;
};

export const resolveFlow = (modeName, selectedKeys = []) => {
	const mode = getStepConfig(modeName);
	const flatten = mode?.flatten === true;
	if (!(mode?.categories || []).length) {
		return { flatten, queue: [null], merged: [] };
	}
	const driverKey = primaryCategoryKey(selectedKeys);
	return {
		flatten,
		queue: flatten ? [driverKey] : selectedKeys,
		merged: flatten
			? selectedKeys.filter((key) => (mode?.categories || []).includes(key))
			: selectedKeys,
	};
};